import type { LucideIcon } from 'lucide-react'
import type { CategoryKey, CoverKey, SplitMode } from '@/types'

import {
    BedDouble,
    Receipt,
    ShoppingBag,
    Ticket,
    TrainFront,
    UtensilsCrossed
} from 'lucide-react'

export type CategoryOption = {
    key: CategoryKey
    label: string
    icon: LucideIcon
}

export const CATEGORIES: CategoryOption[] = [
    { key: 'lodging', label: 'Stay', icon: BedDouble },
    { key: 'food', label: 'Food & drink', icon: UtensilsCrossed },
    { key: 'transport', label: 'Getting around', icon: TrainFront },
    { key: 'activities', label: 'Activities', icon: Ticket },
    { key: 'shopping', label: 'Shopping', icon: ShoppingBag },
    { key: 'other', label: 'Other', icon: Receipt }
]

export const CATEGORY_KEYS = CATEGORIES.map(category => category.key)

export const getCategory = (key: string): CategoryOption => {
    return CATEGORIES.find(category => category.key === key) ?? CATEGORIES[CATEGORIES.length - 1]
}

export const SPLIT_MODES: { key: SplitMode, label: string, hint: string }[] = [
    { key: 'equal', label: 'Equally', hint: 'Everyone ticked pays the same.' },
    { key: 'exact', label: 'Exact amounts', hint: 'Type what each person owes.' },
    { key: 'shares', label: 'By shares', hint: 'A couple can count as 2 shares.' }
]

export const CURRENCIES: { code: string, name: string }[] = [
    { code: 'USD', name: 'US dollar' },
    { code: 'EUR', name: 'Euro' },
    { code: 'GBP', name: 'British pound' },
    { code: 'CAD', name: 'Canadian dollar' },
    { code: 'AUD', name: 'Australian dollar' },
    { code: 'NZD', name: 'New Zealand dollar' },
    { code: 'CHF', name: 'Swiss franc' },
    { code: 'SEK', name: 'Swedish krona' },
    { code: 'NOK', name: 'Norwegian krone' },
    { code: 'DKK', name: 'Danish krone' },
    { code: 'PLN', name: 'Polish zloty' },
    { code: 'CZK', name: 'Czech koruna' },
    { code: 'TRY', name: 'Turkish lira' },
    { code: 'AED', name: 'UAE dirham' },
    { code: 'INR', name: 'Indian rupee' },
    { code: 'THB', name: 'Thai baht' },
    { code: 'SGD', name: 'Singapore dollar' },
    { code: 'HKD', name: 'Hong Kong dollar' },
    { code: 'JPY', name: 'Japanese yen' },
    { code: 'KRW', name: 'South Korean won' },
    { code: 'CNY', name: 'Chinese yuan' },
    { code: 'IDR', name: 'Indonesian rupiah' },
    { code: 'VND', name: 'Vietnamese dong' },
    { code: 'MXN', name: 'Mexican peso' },
    { code: 'BRL', name: 'Brazilian real' },
    { code: 'ZAR', name: 'South African rand' }
]

export const CURRENCY_CODES = CURRENCIES.map(currency => currency.code)

export const COVERS: { key: CoverKey, label: string, image: string, alt: string }[] = [
    { key: 'city', label: 'City break', image: '/images/trips/city-break.jpg', alt: 'Pastel buildings and terracotta roofs in a European old town' },
    { key: 'beach', label: 'Beach', image: '/images/trips/beach.jpg', alt: 'A golden beach and turquoise waves seen from above' },
    { key: 'mountains', label: 'Mountains', image: '/images/trips/mountains.jpg', alt: 'Hikers crossing a rocky, snowy mountain landscape' },
    { key: 'road', label: 'Road trip', image: '/images/trips/road-trip.jpg', alt: 'A green camper van parked on a desert road' },
    { key: 'snow', label: 'Snow', image: '/images/trips/ski-weekend.jpg', alt: 'A wooden cabin glowing in a snowy forest' },
    { key: 'festival', label: 'Festival', image: '/images/trips/festival.jpg', alt: 'Two friends laughing under colourful flags at a festival' }
]

export const COVER_KEYS = COVERS.map(cover => cover.key)

export const getCover = (key: string | null | undefined) => {
    return COVERS.find(cover => cover.key === key) ?? COVERS[0]
}

export const LIMITS = {
    tripName: 60,
    destination: 60,
    travelerName: 30,
    travelers: 30,
    expenseTitle: 80,
    expenseNotes: 500,
    expenses: 2000,
    receiptBytes: 4 * 1024 * 1024,
    maxAmountCents: 100_000_000_00,
    maxWeight: 100
}

export const SAMPLE_TRIP_CODE = 'lisbon-sample'
